import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { LogReturnedPaymentProps, LogReturnedPaymentStatus } from './LogReturnedPayment.types'
import {
  ActionModal,
  Modal,
  Text,
  Spacer,
  Flex,
  AcknowledgeModal,
  FormField,
  Select,
  TextArea
} from '@percent/lemonade'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useLogReturnedPaymentForm } from './useLogReturnedPaymentForm/useLogReturnedPaymentForm'

export function LogReturnedPayment({
  isOpen,
  onClose,
  disbursement,
  currencyInfo,
  refreshData
}: LogReturnedPaymentProps) {
  const { disbursementsService } = useServices()
  const { t } = useTranslation()

  const [logReturnedPaymentStatus, setLogReturnedPaymentStatus] = useState<LogReturnedPaymentStatus | null>(null)

  const [{ isPending }, { apiFunc: logReturnedPayment }] = useMutation(
    disbursementsService.logReturnedPayment,
    () => {
      setLogReturnedPaymentStatus(LogReturnedPaymentStatus.SUCCESS)
      refreshData?.()
    },
    () => setLogReturnedPaymentStatus(LogReturnedPaymentStatus.ERROR)
  )

  const { formik, reasonOptions } = useLogReturnedPaymentForm({
    onFormSubmit: async ({ reason }) => logReturnedPayment({ disbursementId: disbursement.id, reason })
  })

  const onReasonChange = (reason: string) => {
    setSelectedReason(reason)
    formik.setFieldValue('returnReason', reason)
  }

  const { values, touched, errors, isValid, handleChange, handleBlur } = formik

  const [selectedReason, setSelectedReason] = useState<string>(reasonOptions[0].value)

  const successModal = logReturnedPaymentStatus === LogReturnedPaymentStatus.SUCCESS && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.logReturnedPayment.success.title')}
      description={t('dialog.logReturnedPayment.success.description')}
      buttonText={t('button.close')}
      handleClose={onClose}
      viewTestId="log-returned-payment-success-modal"
      buttonTestId="close-log-returned-payment-success-modal"
    />
  )

  const errorModal = logReturnedPaymentStatus === LogReturnedPaymentStatus.ERROR && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.logReturnedPayment.error.title')}
      buttonText={t('button.done')}
      handleClose={onClose}
      viewTestId="log-returned-payment-error-modal"
      buttonTestId="close-log-returned-payment-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={onClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('typography.logReturnedPayment')}
          primaryButtonText={t('button.confirm')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          handleClose={onClose}
          handleSubmit={() => formik.handleSubmit()}
          primaryBtnTestId="log-returned-payment-submit-button"
          secondaryBtnTestId="log-returned-payment-cancel-button"
          disabled={!(isValid && !isPending)}
          loading={isPending}
        >
          <div>
            <Flex direction="column" gap={6}>
              <Text size="small">{t('dialog.logReturnedPayment.description')}</Text>
              <Text size="small">
                {disbursement.organisationName} {' | '}
                {formatAmountFromMinorUnits({
                  currencyCode: disbursement.foundationCurrencyCode,
                  value: disbursement.foundationAmount,
                  currencies: currencyInfo
                })}{' '}
                ({disbursement.foundationCurrencyCode})
              </Text>
            </Flex>
            <Spacer axis="vertical" size={6} />
            <Text size="small">{t('dialog.logReturnedPayment.selectReasonForReturn')}</Text>

            <Spacer axis="vertical" size={6} />
            <form>
              <FormField
                label={t('dialog.logReturnedPayment.returnReason')}
                data-testid="returnReason"
                necessity="required"
              >
                <Select
                  data-testid="returnReasonSelect"
                  placeholder={t('dialog.logReturnedPayment.returnReason')}
                  options={reasonOptions}
                  defaultValue={reasonOptions.find(option => option.value === values.returnReason)}
                  onChange={({ value }) => onReasonChange(value)}
                  maxDropdownHeight="200px"
                />
              </FormField>
              <Spacer axis="vertical" size={6} />
              {selectedReason === 'Other' && (
                <FormField
                  label={t('dialog.logReturnedPayment.customReason')}
                  necessity="required"
                  status={touched.customReason && errors.customReason ? 'danger' : 'default'}
                  statusMessage={errors.customReason || ''}
                  data-testid="customReason"
                >
                  <TextArea
                    name="customReason"
                    value={values.customReason || ''}
                    placeholder={t('dialog.logReturnedPayment.customReasonPlaceholder')}
                    maxLength={255}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
              )}
            </form>
          </div>
        </ActionModal>
      )}
    </Modal>
  )
}
